import type { BackofficeSale } from '@/api/services/retail-wizard/contract';
import { soldCentsOf } from '@/api/services/retail-wizard/soldAmount';
import { saleCustomerLabel } from '@/shared/lib/format/saleCustomer';
import {
    hasEquipment,
    NO_PHONE_LABEL,
    ticketTitle,
} from '@/shared/patterns/SaleTicket/ticketSummary';
import { centralStatusView } from './centralStatus';

const COLUMNAS = [
    'Folio',
    'Fecha',
    'Cliente',
    'CURP',
    'Producto',
    'Tienda',
    'Vendedor',
    'Financiera',
    'Vendido',
    'Enganche',
    'Cobrado',
    'Estado',
    'Registro La Central',
    'Cancelación La Central',
] as const;

/** Escapa según RFC 4180: si el valor trae coma, comilla o salto, va entre comillas y las duplica. */
function esc(v: string | null): string {
    const s = v ?? '';
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Marca y modelo del equipo; sin teléfono, «Sin teléfono» con las líneas del ticket. */
function productCell(s: BackofficeSale): string | null {
    if (!hasEquipment(s)) return ticketTitle(s, NO_PHONE_LABEL);
    return [s.productBrand, s.productModel].filter(Boolean).join(' ') || null;
}

/**
 * ⚠️ El CSV contiene PII (nombre y CURP). Su descarga exige el mismo permiso que la lista y genera
 * PiiAccessLog en el backend. No agregar columnas nuevas sin revisar esa consecuencia.
 */
export function salesToCsv(sales: BackofficeSale[]): string {
    const filas = sales.map((s) =>
        [
            esc(s.folio),
            esc(s.soldAt),
            esc(saleCustomerLabel(s)),
            esc(s.customerCurp),
            esc(productCell(s)),
            esc(s.storeName),
            esc(s.sellerName),
            // Mismo criterio que la tabla: el crédito propio no tiene `externalProvider` porque
            // SureKeep no es un tercero, pero la financiera de esa venta ES SureKeep.
            esc(s.externalProvider ?? (s.type === 'CREDIT' ? 'SureKeep' : 'Contado')),
            // Vendido es el mismo monto de la tabla; enganche y cobrado van aparte.
            (soldCentsOf(s) / 100).toFixed(2),
            s.downPaymentCents !== null ? (s.downPaymentCents / 100).toFixed(2) : '',
            (s.amountCents / 100).toFixed(2),
            esc(s.status),
            esc(centralStatusView(s.centralSyncStatus).defaultLabel),
            esc(centralStatusView(s.centralCancelStatus).defaultLabel),
        ].join(','),
    );

    return [COLUMNAS.join(','), ...filas].join('\n');
}
